import { useEffect, useState } from 'react';
import { Clock, Users, Activity, Calendar } from 'lucide-react';
import StatsCard from '../components/StatsCard';
import LeaderboardTable from '../components/LeaderboardTable';

interface ProductivityStats {
  userProductivity: {
    [key: string]: {
      totalHours: number;
      days: number;
      avgHoursPerDay: string;
    };
  };
  recentActivities: Array<{
    action: string;
    entity_type: string;
    created_at: string;
    users: {
      name: string;
    };
  }>;
}

export default function EstatisticasProdutividade() {
  const [stats, setStats] = useState<ProductivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadStats();
  }, [filter]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const params = new URLSearchParams();
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);

      const res = await fetch(`${apiUrl}/api/stats/productivity?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas de produtividade:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  // Calcular totais
  const totalHours = Object.values(stats?.userProductivity || {}).reduce(
    (sum, user) => sum + user.totalHours, 0
  );
  const totalDays = Object.values(stats?.userProductivity || {}).reduce(
    (sum, user) => sum + user.days, 0
  );
  const activeUsers = Object.keys(stats?.userProductivity || {}).length;
  const avgHoursPerUser = activeUsers > 0 ? totalHours / activeUsers : 0;

  // Preparar dados para ranking
  const productivityData = Object.entries(stats?.userProductivity || {}).map(([name, data], index) => ({
    id: `user-${index}`,
    name,
    value: data.totalHours,
    subtitle: `${data.days} dias • Média: ${data.avgHoursPerDay}h/dia`
  }));

  const formatDate = (dateString: string) => {
    const dateStr = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      'create': 'Criou',
      'update': 'Atualizou',
      'delete': 'Deletou',
      'login': 'Login',
      'logout': 'Logout'
    };
    return labels[action] || action;
  };

  const getEntityLabel = (entity: string) => {
    const labels: { [key: string]: string } = {
      'user': 'Usuário',
      'sale': 'Venda',
      'chat': 'Chat',
      'attendance': 'Atendimento'
    };
    return labels[entity] || entity;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Estatísticas de Produtividade</h1>
        <p className="text-gray-600">Horas trabalhadas e atividades do sistema</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtrar Período</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={filter.start_date}
              onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={filter.end_date}
              onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({
                start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                end_date: new Date().toISOString().split('T')[0]
              })}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Últimos 30 dias
            </button>
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatsCard
          title="Total de Horas"
          value={`${totalHours.toFixed(2)}h`}
          icon={Clock}
          iconColor="text-blue-500"
        />
        
        <StatsCard
          title="Usuários Ativos"
          value={activeUsers}
          icon={Users}
          iconColor="text-green-500"
        />
        
        <StatsCard
          title="Média por Usuário"
          value={`${avgHoursPerUser.toFixed(2)}h`}
          icon={Activity}
          iconColor="text-purple-500"
        />
        
        <StatsCard
          title="Total de Dias"
          value={totalDays}
          icon={Calendar}
          iconColor="text-orange-500"
        />
      </div>

      {/* Grid de Conteúdo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ranking de Produtividade */}
        <LeaderboardTable
          title="Ranking de Horas Trabalhadas"
          data={productivityData}
          valueLabel="Total de Horas"
          valueFormatter={(value) => `${value.toFixed(2)}h`}
        />

        {/* Atividades Recentes */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Atividades Recentes</h3>
          </div>
          
          <div className="max-h-[500px] overflow-y-auto">
            {stats?.recentActivities && stats.recentActivities.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {stats.recentActivities.map((activity, index) => (
                  <div key={index} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-start">
                      <Activity className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">
                          <span className="font-semibold">{activity.users?.name || 'Usuário'}</span>
                          {' '}{getActionLabel(activity.action)}{' '}
                          <span className="text-gray-600">{getEntityLabel(activity.entity_type)}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                Nenhuma atividade registrada
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
